import { style, keyframes } from '@vanilla-extract/css'
import { recipe } from '@vanilla-extract/recipes'
import { vars } from '../theme.css'

const tone = {
  ink: { color: vars.color.ink },
  ink2: { color: vars.color.ink2 },
  soft: { color: vars.color.inkSoft },
  red: { color: vars.color.red },
  amber: { color: vars.color.amber },
  green: { color: vars.color.green },
} as const

// Text — serif editorial prose. The default reading voice.
export const text = recipe({
  base: { fontFamily: vars.font.serif, color: vars.color.ink, margin: 0, lineHeight: vars.lh.normal },
  variants: {
    size: {
      lg: { fontSize: vars.fs.lg },
      md: { fontSize: vars.fs.md },
      sm: { fontSize: vars.fs.sm },
      base: { fontSize: vars.fs.base },
    },
    tone,
    leading: {
      tight: { lineHeight: vars.lh.tight },
      snug: { lineHeight: vars.lh.snug },
      normal: { lineHeight: vars.lh.normal },
      relaxed: { lineHeight: vars.lh.relaxed },
    },
    weight: { normal: { fontWeight: 400 }, medium: { fontWeight: 500 } },
    italic: { true: { fontStyle: 'italic' } },
  },
  defaultVariants: { size: 'md', tone: 'ink' },
})

// Heading — serif display. Scale carries the editorial hierarchy.
export const heading = recipe({
  base: {
    fontFamily: vars.font.serif,
    color: vars.color.ink,
    margin: 0,
    lineHeight: vars.lh.tight,
    fontWeight: 500,
    letterSpacing: '-0.01em',
  },
  variants: {
    level: {
      '3xl': { fontSize: vars.fs['3xl'] },
      '2xl': { fontSize: vars.fs['2xl'] },
      xl: { fontSize: vars.fs.xl },
      lg: { fontSize: vars.fs.lg },
    },
    tone,
    weight: { normal: { fontWeight: 400 }, medium: { fontWeight: 500 } },
    italic: { true: { fontStyle: 'italic' } },
  },
  defaultVariants: { level: 'xl' },
})

// Label — mono utility label, small but not anaemic. Uppercase + tracked.
export const label = recipe({
  base: {
    fontFamily: vars.font.mono,
    fontWeight: 500,
    color: vars.color.inkSoft,
    margin: 0,
    textTransform: 'uppercase',
    letterSpacing: vars.ls.label,
    lineHeight: vars.lh.snug,
  },
  variants: {
    // ONE size. A label is `ms.sm` by the ladder above; the md and xs variants existed only so
    // one page could set the same role a pixel bigger and a pixel smaller than everywhere else,
    // and both emitted CSS nothing wanted.
    size: { sm: { fontSize: vars.ms.sm } },
    tone,
    tracked: { true: { letterSpacing: vars.ls.caps } },
  },
  defaultVariants: { size: 'sm', tone: 'soft' },
})

// Count — mono numerals, tabular so columns align.
export const count = recipe({
  base: { fontFamily: vars.font.mono, color: vars.color.ink, fontVariantNumeric: 'tabular-nums', margin: 0 },
  variants: {
    size: { lg: { fontSize: vars.ms.lg }, md: { fontSize: vars.ms.md }, sm: { fontSize: vars.ms.sm } },
    tone,
  },
  defaultVariants: { size: 'md', tone: 'ink' },
})

// ---------------------------------------------------------------------------
// THE MONO LADDER. Three ranks, and which one a thing takes is decided by WHAT IT IS, not by
// which module drew it. Before this the same job was set at 10, 11 and 12px with either tracking
// in ten modules, so a stat label read one way in the species sheet, another in Admin and a third
// in Stats.
//
//   kicker / sectionHead   ms.sm + ls.caps   an EDITORIAL head — an eyebrow, a section head, the
//                                            masthead nav. Widely tracked, because it is display.
//   utility                ms.sm + ls.label  an INSTRUMENT LABEL — a column head, a control's
//                                            label, a stat's caption, a credit's tag.
//   reading                ms.md, untracked  a READING — a count, a time, a measurement. Tabular,
//                                            not uppercase: it is a figure, not a word.
//
// ms.xs (10px) is left to ONE job: a chart's axis tick, where the label has to fit between bars.
//
// WEIGHT follows the same division: a LABEL is 500 (it names something), a CONTROL and a READING
// are 400 (they are a thing you press and a figure you read). Before this the split ran the other
// way in four places — the Stats column heads were 500 and the identical labels beside Live's
// news were 400.
// ---------------------------------------------------------------------------

// THE utility label. Every small uppercase mono label that is not an editorial head: the Stats
// column heads, the Directory's SHOW/SORT, the species sheet's stat captions and lore kickers,
// the Journal's dateline and record terms, Admin's figure captions.
export const utility = style({
  margin: 0,
  fontFamily: vars.font.mono,
  fontWeight: 500,
  fontSize: vars.ms.sm,
  letterSpacing: vars.ls.label,
  textTransform: 'uppercase',
  lineHeight: vars.lh.snug,
  color: vars.color.inkSoft,
})

// THE reading. A number, a clock time, a measurement — the instrument voice. Tabular figures so
// a column of them lines up, and NO tracking or uppercasing: those belong to words. The almanac
// row, the Journal record's values, the figures line and the detections roll each carried their
// own copy, and only some of them were tabular — so "14–19 °C" and "0.3 mm" sat in one strip
// with figures of two different widths.
export const reading = style({
  margin: 0,
  fontFamily: vars.font.mono,
  fontSize: vars.ms.md,
  fontVariantNumeric: 'tabular-nums',
  color: vars.color.ink,
})

// THE chart axis tick — the one place 10px is right, because the label sits under a bar.
export const axisTick = style({
  fontFamily: vars.font.mono,
  fontSize: vars.ms.xs,
  fontVariantNumeric: 'tabular-nums',
  color: vars.color.inkSoft,
  whiteSpace: 'nowrap',
})

// ---------------------------------------------------------------------------
// THE BIRD'S NAME, in two ranks. A bird is named on six surfaces and was set six ways — 19px/500
// on a Directory plate, 17px/400 in Live's roll, 17px italic in the Journal record, and an
// unstyled 16px in the Stats table, which simply inherited the page. The RANK is a real
// distinction (a plate's name titles the item; a row's name labels a line); the divergence
// inside each rank was not.
// ---------------------------------------------------------------------------
const birdNameBase = {
  fontFamily: vars.font.serif,
  fontStyle: 'normal',
  lineHeight: vars.lh.snug,
  color: vars.color.ink,
} as const
// A name that TITLES its item: the Directory plate.
export const birdNamePlate = style({ ...birdNameBase, fontWeight: 500, fontSize: vars.fs.md })
// A name that LABELS a row: Live's roll, the Journal rail and record, the Stats table.
export const birdNameRow = style({ ...birdNameBase, fontWeight: 400, fontSize: vars.fs.sm })

// THE binomial. Serif italic, one size, one tone — it was inkSoft in the Directory, ink2 in the
// species sheet, and in the Journal it inherited a MONO caption's 11px, so the one piece of type
// on the site that is always Latin was also the one set at a size from the other scale.
export const sciName = style({
  fontFamily: vars.font.serif,
  fontStyle: 'italic',
  fontSize: vars.fs.sm,
  fontWeight: 400,
  color: vars.color.inkSoft,
})

// THE editorial eyebrow — the small tracked mono line above a headline or heading a section.
// There were five copies of these exact values (the Journal's kicker AND its rubric, Live's
// .section-tag at 9px/700, the About lead's eyebrow at 12px/0.2em, the design system's own
// Kicker), which is why the same role read at four different sizes across four pages.
export const kicker = style({
  margin: 0,
  fontFamily: vars.font.mono,
  fontWeight: 500,
  fontSize: vars.ms.sm,
  letterSpacing: vars.ls.caps,
  textTransform: 'uppercase',
  lineHeight: vars.lh.snug,
  color: vars.color.ink2,
})

// THE section head — the mono heading that opens a ruled block: the Journal rail's "The record",
// the account drawer's settings groups, the admin console's pane, the species sheet's record.
// Same four values in four modules at two sizes, two colours and two weights before this. The
// RULE under it stays with the module, because its weight is that surface's business: the
// Journal's rail heads take an ink rule (a newspaper column head), everything else a soft one.
export const sectionHead = style({
  margin: 0,
  fontFamily: vars.font.mono,
  fontWeight: 500,
  fontSize: vars.ms.sm,
  letterSpacing: vars.ls.caps,
  textTransform: 'uppercase',
  lineHeight: vars.lh.snug,
  color: vars.color.ink,
})

// THE secondary-language gloss. A bird's other-language name, wherever it trails the primary
// one: the roll on Live, the Journal's rail, the Directory plate, the Stats table, the account
// list. Five surfaces had five treatments (0.82em / 0.88em / unsized <em> / a block line /
// parenthesised), which is what made a bilingual row look different on every page.
export const gloss = style({
  fontFamily: vars.font.serif,
  fontStyle: 'italic',
  fontSize: vars.fs.xs,
  color: vars.color.inkSoft,
  fontWeight: 400,
})

// THE source mark — how this publication sets a link to a source: no underline, a hairline that
// darkens on hover. The Journal's citation tails, the species sheet's lore credits and Live's
// citation line each carried their own copy of these five declarations.
export const sourceMark = {
  color: vars.color.inkSoft,
  textDecoration: 'none',
  borderBottom: `1px solid ${vars.color.rule}`,
  paddingBottom: '1px',
  transition: `color ${vars.dur.fast} ease, border-bottom-color ${vars.dur.fast} ease`,
} as const
export const sourceMarkHover = {
  color: vars.color.ink,
  borderBottomColor: vars.color.inkSoft,
} as const

// THE state line — loading, empty, offline, error. One voice for "there is nothing here yet",
// so an empty Directory, an empty Stats period and a Journal with no completed day read as the
// same publication saying the same kind of thing. Reserved geometry, no spinner (§8).
export const stateLine = style({
  margin: 0,
  padding: `${vars.space[8]} ${vars.space[4]}`,
  textAlign: 'center',
  fontFamily: vars.font.serif,
  fontSize: vars.fs.md,
  lineHeight: vars.lh.normal,
  color: vars.color.inkSoft,
})

// The one busy mark in the app: a slowly turning line-icon on a control that is working.
// Honours prefers-reduced-motion — it simply stops rather than disappearing.
export const spin = keyframes({ to: { transform: 'rotate(360deg)' } })
export const spinning = style({
  animation: `${spin} 900ms linear infinite`,
  '@media': { '(prefers-reduced-motion: reduce)': { animation: 'none' } },
})
